import React, { useCallback, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import { openModal, closeModal } from 'components/Modal';
import { createSuccessSnackbar } from 'components/Snackbar';
import useModifierItemLinks from 'features/Sku/Modifiers/useModifierItemLinks';
import { reportError } from 'helpers/error-logging';
import Segment from 'helpers/segment';
import { selectUserInfo } from 'selectors/general';

import { ITEM_SAVE_STATUS } from './SkuItems.constants';
import { createSkuItem, linkItemToSubgroup, submitSkuMenuPricing, unlinkItemFromSubgroup } from './SkuItems.helpers';
import { getSkuVenueSource } from './SkuVenueSource.helpers';
import { SKU_ANALYTICS_EVENTS } from './SkuAnalytics.constants';
import { getCategoryId, getItemId } from './SkuMenuData.normalizers';
import { messages } from './components/SkuAddItemForm.messages';
import { SKU_ITEM_IN_REVIEW_MODAL_NAME } from './components/SkuItemInReviewModal';
import { shouldShowInReviewModal } from './components/SkuItemInReviewModal.helpers';

const ITEM_SUBMIT_ERROR_MODAL_NAME = 'ItemSubmitErrorModal';

const useSkuItemSubmission = ({ venue, currencyCode, onSuccess }) => {
  const dispatch = useDispatch();
  const { adminUserId } = useSelector(selectUserInfo);
  const isAdminUser = !!adminUserId;
  const { closeFlyout } = useContext(FlyoutContext);
  const { venueId } = venue;
  const source = getSkuVenueSource(venue);
  const { submitGroupLinksForItem } = useModifierItemLinks({ venueId });

  const handleCreateItem = useCallback(
    async values => {
      const { itemName: name, itemDescription: description, categories, menuPrice: price, linkToMenu: link } = values;

      const result = await createSkuItem(venueId, {
        name,
        description,
        enabled: true,
        soldOut: false,
      });

      const itemId = getItemId(result?.item) || getItemId(result);

      if (!itemId) {
        throw new Error('Created SKU item response is missing item id');
      }

      Segment.event(SKU_ANALYTICS_EVENTS.ITEM_CREATED, { venueId, itemId, source });

      return { itemId, name, categories, price, link };
    },
    [venueId, source]
  );

  const handleLinkCategories = useCallback(
    async (itemId, selectedCategories, checkpoint) => {
      const selectedIds = selectedCategories.map(getCategoryId);
      const toLink = selectedCategories.filter(cat => !checkpoint.linkedCategoryIds.has(getCategoryId(cat)));
      const toUnlinkIds = [...checkpoint.linkedCategoryIds].filter(categoryId => !selectedIds.includes(categoryId));

      const operations = [
        ...toLink.map(cat => ({
          categoryId: getCategoryId(cat),
          kind: 'link',
          run: () => linkItemToSubgroup(venueId, getCategoryId(cat), itemId, selectedIds.indexOf(getCategoryId(cat))),
        })),
        ...toUnlinkIds.map(categoryId => ({
          categoryId,
          kind: 'unlink',
          run: () => unlinkItemFromSubgroup(venueId, categoryId, itemId),
        })),
      ];
      if (operations.length === 0) return { failed: false };

      const linkResults = await Promise.allSettled(operations.map(operation => operation.run()));
      const linkFailures = linkResults.filter(result => result.status === 'rejected');
      if (linkFailures.length > 0) {
        reportError('Some category links failed', { failures: linkFailures.map(f => f.reason) });
      }

      operations.forEach((operation, index) => {
        if (linkResults[index].status !== 'fulfilled') return;
        if (operation.kind === 'link') {
          checkpoint.linkedCategoryIds.add(operation.categoryId);
          Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_ITEM_ADDED, {
            venueId,
            categoryId: operation.categoryId,
            itemId,
            source,
          });
        } else {
          checkpoint.linkedCategoryIds.delete(operation.categoryId);
        }
      });

      return { failed: linkFailures.length > 0 };
    },
    [venueId, source]
  );

  const handleSubmitPricing = useCallback(
    async ({ name, price, link }) => {
      if (isAdminUser) return true;

      try {
        await submitSkuMenuPricing(venue, { itemName: name, menuPrice: price, linkToMenu: link });
        return true;
      } catch (error) {
        reportError('Could not submit menu pricing for SKU Item', error);
        return false;
      }
    },
    [isAdminUser, venue]
  );

  // The item was already saved, so the caller's success path still runs; only the pricing
  // needs another try, which is what the dialog says.
  const showPricingFailure = useCallback(
    ({ name, price, link }) => {
      onSuccess?.();
      closeFlyout();
      dispatch(closeModal());
      dispatch(
        openModal(ITEM_SUBMIT_ERROR_MODAL_NAME, {
          currencyCode,
          itemName: name,
          linkToMenu: link,
          menuPrice: price,
        })
      );
    },
    [onSuccess, closeFlyout, dispatch, currencyCode]
  );

  // Survives across attempts: once the item exists, a retry must never create it again,
  // and only the links still missing (from the form state at retry time) are re-run.
  const checkpointRef = useRef(null);

  const handleSubmit = useCallback(
    async values => {
      const { itemName: name, categories = [], menuPrice: price, linkToMenu: link } = values;

      try {
        // A checkpoint belongs to the venue it was created for; a venue switch mid-flyout must
        // start over rather than link another venue's item.
        if (checkpointRef.current && checkpointRef.current.venueId !== venueId) {
          checkpointRef.current = null;
        }
        if (!checkpointRef.current) {
          try {
            const { itemId } = await handleCreateItem(values);
            checkpointRef.current = {
              venueId,
              itemId,
              linkedCategoryIds: new Set(),
              linkedGroupIds: new Set(),
              pricingSucceededBefore: false,
            };
          } catch (error) {
            reportError('Error creating SKU item', error);
            return { status: ITEM_SAVE_STATUS.NOTHING_SAVED };
          }
        }
        const checkpoint = checkpointRef.current;
        const { itemId } = checkpoint;

        // Each phase isolates its own failures and never rejects; that invariant is what makes
        // Promise.all safe here.
        const selectedGroupIds = values.modifierGroupIds || [];
        const [categoryOutcome, groupOutcome, pricingSucceeded] = await Promise.all([
          handleLinkCategories(itemId, categories, checkpoint),
          submitGroupLinksForItem({
            itemId,
            linked: selectedGroupIds.filter(groupId => !checkpoint.linkedGroupIds.has(groupId)),
            unlinked: [...checkpoint.linkedGroupIds].filter(groupId => !selectedGroupIds.includes(groupId)),
          }),
          // Every submission opens a real Salesforce case, so a retry after a partial save must
          // not repeat one that already went through.
          checkpoint.pricingSucceededBefore ? Promise.resolve(true) : handleSubmitPricing({ name, price, link }),
        ]);
        groupOutcome.linkedGroupIds.forEach(groupId => checkpoint.linkedGroupIds.add(groupId));
        groupOutcome.unlinkedGroupIds.forEach(groupId => checkpoint.linkedGroupIds.delete(groupId));
        checkpoint.pricingSucceededBefore = checkpoint.pricingSucceededBefore || pricingSucceeded;

        const categoriesFailed = categoryOutcome.failed;
        const modifiersFailed = groupOutcome.failedGroupIds.length > 0;
        if (categoriesFailed || modifiersFailed) {
          // A failed pricing stays un-checkpointed and simply retries with the links; announcing
          // it here would close the flyout under the partial-save banner.
          return { status: ITEM_SAVE_STATUS.PARTIALLY_SAVED, categoriesFailed, modifiersFailed };
        }

        if (!pricingSucceeded) {
          showPricingFailure({ name, price, link });
          checkpointRef.current = null;
          return { status: ITEM_SAVE_STATUS.SAVED };
        }

        onSuccess?.({ itemId, itemName: name, menuPrice: price, linkToMenu: link });
        closeFlyout();

        if (shouldShowInReviewModal()) {
          dispatch(openModal(SKU_ITEM_IN_REVIEW_MODAL_NAME, { itemName: name }));
        } else {
          dispatch(
            createSuccessSnackbar(
              messages.successAddItem,
              {
                itemName: name,
                strong: chunks => <strong>{chunks}</strong>,
              },
              { isLowStyle: true }
            )
          );
        }
        checkpointRef.current = null;
        return { status: ITEM_SAVE_STATUS.SAVED };
      } catch (error) {
        // Nothing on the happy path should throw past the per-step handling above; if something
        // does, the checkpoint was deliberately not cleared yet, so a retry resumes instead of
        // re-creating the item.
        reportError('Error creating SKU item', error);
        return { status: ITEM_SAVE_STATUS.NOTHING_SAVED };
      }
    },
    [
      venueId,
      handleCreateItem,
      handleLinkCategories,
      submitGroupLinksForItem,
      handleSubmitPricing,
      showPricingFailure,
      onSuccess,
      closeFlyout,
      dispatch,
    ]
  );

  return { handleSubmit };
};

export default useSkuItemSubmission;
